import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { UserModel } from '../models/user.model';
import { clearAuthSync, isSsoActive } from 'src/app/auth/auth-sync';
import { logout as ssoLogout } from 'src/app/auth/sso';

export type UserType = UserModel | undefined;

export const ROLE_SUPERUSER = 'Superuser';
export const ROLE_TENANT_ADMIN = 'TenantAdmin';
export const ROLE_TENANT_USER = 'TenantUser';

/**
 * Session facade for the shell. The identity provider authenticates (see
 * src/app/auth/sso.ts); the portal API decides role and tenant (`GET /me`,
 * applied at boot). Everything here reads the localStorage mirror.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  currentUserSubject = new BehaviorSubject<UserType>(undefined);
  currentUserToken$: Observable<UserType> = this.currentUserSubject.asObservable();
  isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();
  currentUserRole = new BehaviorSubject<string>(this.getStoredRole());

  private interceptorEnabled = true;

  constructor(private router: Router) {}

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  enableInterceptor(): void {
    this.interceptorEnabled = true;
  }

  isInterceptorEnabled(): boolean {
    return this.interceptorEnabled;
  }

  /** Hydrate the session from the localStorage mirror written at boot. */
  getUserByToken(): Observable<UserType> {
    this.currentUserRole.next(this.getStoredRole());
    const token = localStorage.getItem('authToken');
    if (!token) {
      this.currentUserSubject.next(undefined);
      return of(undefined);
    }

    const user = new UserModel();
    user.id = localStorage.getItem('portalUserId');
    user.email = localStorage.getItem('userEmail') ?? '';
    user.fullName = localStorage.getItem('userDisplayName') ?? user.email;
    user.role = this.getStoredRole();
    user.tenantId = localStorage.getItem('tenantId');
    user.tenantName = localStorage.getItem('tenantName');
    this.currentUserSubject.next(user);
    return of(user);
  }

  getRole(): Observable<string> {
    return this.currentUserRole.asObservable();
  }

  get role(): string {
    return this.currentUserRole.value;
  }

  isSuperuser(): boolean {
    return this.role === ROLE_SUPERUSER;
  }

  isTenantAdmin(): boolean {
    return this.role === ROLE_TENANT_ADMIN;
  }

  /** Superuser or tenant administrator: may create/edit/delete inside their scope. */
  canManage(): boolean {
    return this.isSuperuser() || this.isTenantAdmin();
  }

  /** Whether listings render the action column for this module. */
  canShowActions(_module: string): boolean {
    return this.canManage();
  }

  checkUserRole(): boolean {
    return this.isSuperuser();
  }

  logout(): void {
    const sso = isSsoActive();
    clearAuthSync();
    this.currentUserSubject.next(undefined);
    this.currentUserRole.next('');
    if (sso) {
      ssoLogout();
      return;
    }
    this.router.navigate(['/auth/login']);
  }

  private getStoredRole(): string {
    return localStorage.getItem('userRole') || '';
  }
}
